/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useCustomerCodeList.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Class of Services module.
────────────────────────────────────────────────────────────────*/


import { useCallback, useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../hooks/useNotifications';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchCustomerCodes } from '../api';

export function useCustomerCodeList() {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedTenant, selectedDomain } = useContextStore();
  const tenantSlug = selectedTenant?.slug ?? 'main';
  const effectiveDomainId = selectedDomain?.id ?? null;

  const availableColumns = useMemo(() => ([
    {
      key: 'code',
      label: t('customerCodes.list.code'),
      visible: true,
      sortable: true,
    },
    {
      key: 'description',
      label: t('customerCodes.list.description'),
      visible: true,
      sortable: false,
    },
    {
      key: 'active',
      label: t('customerCodes.list.active'),
      visible: true,
      sortable: true,
    },
    {
      key: 'createdAt',
      label: t('customerCodes.list.createdAt'),
      visible: false,
      sortable: true,
    },
    {
      key: 'actions',
      label: t('customerCodes.list.actions'),
      visible: true,
      sortable: false
    }
  ]), [t]);

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    if (!effectiveDomainId) {
      return {
        data: [],
        pagination: {
          page: 1,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      };
    }

    try {
      const response = await fetchCustomerCodes({
        ...params,
        domainId: effectiveDomainId
      }, tenantSlug);
      return {
        data: response.data || [],
        pagination: response.pagination || {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      } as DataGridFetchResult;
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customerCodes.messages.loadError');
      showError(message);
      throw error;
    }
  }, [effectiveDomainId, showError, t, tenantSlug]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'code',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'customer-codes',
    reloadKey: effectiveDomainId ?? null
  });

  return {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
    tenantSlug
  };
}

